import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import GradientBackground from '@/components/ui/GradientBackground';
import TextReveal from '@/components/ui/TextReveal';
import { motion } from 'framer-motion';

const FAQPage = () => {
  return (
    <div className="bg-background text-foreground relative min-h-screen overflow-hidden">
      <GradientBackground />

      <div className="container mx-auto py-32 px-4 max-w-5xl relative z-10">

        {/* Header */}
        <div className="text-center mb-24">
          <div className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-md mb-6">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Knowledge Base</span>
          </div>
          <TextReveal className="text-5xl md:text-8xl font-black tracking-tighter text-foreground leading-[0.9] mb-8">
            ANSWERS DECODED.
          </TextReveal>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
          >
            Everything you need to know about operating within the K&C Logistics neural network.
          </motion.p>
        </div>

        <div className="space-y-12">
          {/* General Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-foreground/5 border-foreground/10 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-foreground">General Protocol</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">What is K&C Logistics?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      K&C Logistics is a digital platform that connects individuals and businesses (Clients) with reliable truck drivers and fleet owners (Transporters) across Malawi. We facilitate secure payments, real-time tracking, and verified transport services.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">Is the platform free to use?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Signing up is free for everyone. Clients pay the agreed shipping price plus a small service fee. Transporters pay a commission on completed jobs.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-3" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">How do I contact support?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      You can reach our support team via the "Contact Us" page, email us at support@kclogistics.mw, or call our helpline at +265 999 123 456.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </motion.div>

          {/* For Clients */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-foreground/5 border-foreground/10 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-foreground">For Clients</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="client-1" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">How do I post a job?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Simply log in, click "Post a Job" on your dashboard, and fill in the details (pickup/delivery locations, cargo type, price offer). Your job will be instantly visible to verified transporters.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="client-2" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">How do payments work?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Posting a job is free! Once a transporter delivers your cargo, you will pay them the agreed amount <strong className="text-foreground">exactly as agreed</strong> (e.g., Cash or Mobile Money). The platform does not hold your funds.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="client-3" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">Can I track my cargo?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Yes! Once your cargo is "In Transit," you can track the transporter's real-time location directly from your dashboard. You'll also receive status updates via notifications.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="client-4" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">What if my goods are damaged?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      All verified transporters are encouraged to have goods-in-transit insurance. If damage occurs, please report it immediately via the platform so we can assist with the dispute resolution and insurance claim process.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </motion.div>

          {/* For Transporters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-foreground/5 border-foreground/10 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-foreground">For Transporters</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="trans-1" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">How do I become a verified transporter?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Sign up as a Transporter and complete your profile. You must upload a valid Driver's License, Vehicle Registration, and Proof of Ownership. Our team will review your documents within 24-48 hours.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="trans-2" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">When do I get paid?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      You collect the shipping payment directly from the Client upon delivery. Please ensure you agree on the payment method (Cash/Mobile Money) before offloading the cargo.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="trans-fee" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">Is there a fee to accept jobs?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Yes. To secure a job, you must pay a non-refundable <strong className="text-foreground">Booking Fee of MWK 50,000 per load</strong>. This fee is paid to the platform via PayChangu or your Wallet balance.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="trans-3" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">Why do I need to enable location?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Location tracking is required to provide Clients with real-time visibility of their cargo. It also helps protect you by providing a digital record of your journey. Tracking is only active when you are "Online" and have a job "In Transit".
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="trans-4" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">Can I cancel a job?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      You can cancel a job before pickup, but frequent cancellations may affect your rating and account standing. Cancellations after pickup are only allowed in emergencies and must be reported to support.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </motion.div>

          {/* For Fleet Managers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-foreground/5 border-foreground/10 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-foreground">For Fleet Managers</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="fleet-1" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">How do I register my fleet?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Sign up as a Fleet Manager and provide your company details. You can then register multiple vehicles at once by uploading their documentation for bulk verification. Our team reviews fleet registrations within 24-48 hours.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="fleet-2" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">How do I add drivers to my fleet?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      From your Fleet Dashboard, generate unique invite codes for your drivers. They use this code during registration to automatically join your fleet. You can manage all your drivers from a centralized dashboard.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="fleet-3" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">How does payment work for fleet operations?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      All payments for jobs completed by your drivers are received in your fleet wallet. You are responsible for managing payouts to your drivers. The platform provides transparent tracking of all earnings and completed jobs.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="fleet-4" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">Can I assign specific jobs to specific drivers?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Yes! After securing a job, you can assign loads to specific drivers in your fleet through the Dispatch Board. You can see all your drivers, their current status, and assign jobs based on location and availability.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="fleet-5" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">What are the fees for fleet operations?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Fleet Managers pay the same <strong className="text-foreground">MWK 50,000 booking fee per load</strong> when securing jobs. This fee covers all drivers assigned to that job. There are no additional per-driver fees.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </motion.div>

          {/* For Fleet Drivers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
          >
            <Card className="bg-foreground/5 border-foreground/10 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-foreground">For Fleet Drivers</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="driver-1" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">How do I join a fleet?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Get an invite code from your fleet manager and use it during registration at /register/driver. You'll be automatically added to their fleet and can start receiving job assignments immediately after verification.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="driver-2" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">How do I get assigned to jobs?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Your fleet manager assigns jobs to you through the dispatch system. You'll receive notifications when assigned and can view complete job details including pickup location, delivery address, cargo information, and payment amount in your Driver Dashboard.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="driver-3" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">Is location tracking mandatory?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Yes. Location tracking automatically starts when you begin a trip and stops when you complete delivery. This is system-enforced for client transparency and your protection. The tracking provides a digital record of your journey and ensures clients can monitor their cargo.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="driver-4" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">How do I get paid?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Your earnings are managed by your fleet manager. All payments for jobs you complete go to the fleet wallet, and your fleet manager handles driver payouts. You can track all your completed jobs and earnings transparently through your dashboard.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="driver-5" className="border-foreground/10">
                    <AccordionTrigger className="text-lg font-medium hover:text-primary transition-colors">Can I work for multiple fleets?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                      Currently, each driver account can only be associated with one fleet at a time. If you wish to change fleets, please contact support for assistance with the transfer process.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;